import { useState } from 'react';

import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

import {
  jobsApi,
  type JobListKind,
  type JobSummary,
  type RecurringJob,
} from '@/api/queries/jobs';
import { isSuperAdmin, useTenantStore } from '@/auth/tenantStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';
import { useModal } from '@/components/modal/useModal';
import {
  Button,
  Icon,
  IconButton,
  Pagination,
  Select,
  StatCard,
  StatusPill,
  Table,
  useToast,
  type StatusTone,
  type TableColumn,
} from '@/components/ui';
import { formatDateTime } from '@/utils/formatDate';

import { JobDetailModal } from './-components/JobDetailModal';

const JOB_TABS: { value: JobListKind; label: string }[] = [
  { value: 'processing', label: 'Processing' },
  { value: 'enqueued', label: 'Enqueued' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

const searchSchema = z.object({
  tab: z.enum(['processing', 'enqueued', 'scheduled', 'succeeded', 'failed']).catch('processing'),
});

// Requeue only makes sense for jobs that have left the queue — failed jobs and
// terminal-but-replayable ones.
const REQUEUEABLE: JobListKind[] = ['failed', 'succeeded', 'scheduled'];

function jobStateTone(state?: string | null): StatusTone {
  if (state === 'Succeeded') return 'active';
  if (state === 'Failed' || state === 'Deleted') return 'dnc';
  return 'inactive';
}

function JobsRoute() {
  useSetBreadcrumbs([{ label: 'Admin' }, { label: 'Jobs' }]);
  const navigate = useNavigate();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Jobs</h1>
        <span style={{ flex: 1 }} />
        <Button
          variant="secondary"
          leftIcon={<Icon name="settings" size={13} />}
          onClick={() => navigate({ to: '/admin/jobs/settings' })}
        >
          Configure
        </Button>
      </div>
      <StatCards />
      <Section title="Recurring jobs">
        <RecurringTable />
      </Section>
      <Section title="Recent jobs">
        <RecentJobs />
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>{title}</h2>
      {children}
    </div>
  );
}

function StatCards() {
  const { data: stats } = jobsApi.useJobStats();
  const s = stats ?? {
    processing: 0,
    enqueued: 0,
    scheduled: 0,
    succeeded: 0,
    failed: 0,
    servers: 0,
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
      <StatCard label="Processing" value={s.processing} tone="info" icon="refresh" />
      <StatCard label="Enqueued" value={s.enqueued} tone="amber" icon="clock" />
      <StatCard label="Scheduled" value={s.scheduled} icon="calendar" />
      <StatCard label="Succeeded" value={s.succeeded} tone="brand" icon="check" />
      <StatCard label="Failed" value={s.failed} tone="amber" icon="alert" />
      <StatCard label="Servers" value={s.servers} icon="database" />
    </div>
  );
}

function RecurringTable() {
  const { data: jobs, isPending } = jobsApi.useRecurringJobs();
  const trigger = jobsApi.useTriggerRecurring();
  const toast = useToast();

  const onTrigger = async (id: string) => {
    try {
      await trigger.mutateAsync(id);
      toast.show({ title: `Triggered "${id}"`, severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to trigger job',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  const columns: TableColumn<RecurringJob>[] = [
    { id: 'id', header: 'Id', cell: (j) => <span style={{ fontWeight: 500 }}>{j.id}</span> },
    {
      id: 'job',
      header: 'Job',
      cell: (j) => <code style={{ fontSize: 12, color: 'var(--text-3)' }}>{j.job ?? '—'}</code>,
    },
    {
      id: 'cron',
      header: 'Cron',
      cell: (j) => <code style={{ fontSize: 12 }}>{j.cron ?? '—'}</code>,
      width: 140,
    },
    { id: 'queue', header: 'Queue', cell: (j) => j.queue ?? '—', width: 110 },
    {
      id: 'last',
      header: 'Last run',
      cell: (j) => formatDateTime(j.lastExecution) ?? 'Never',
      width: 170,
    },
    {
      id: 'next',
      header: 'Next run',
      cell: (j) => formatDateTime(j.nextExecution) ?? '—',
      width: 170,
    },
    {
      id: 'state',
      header: 'Last state',
      cell: (j) =>
        j.lastJobState ? (
          <StatusPill status={jobStateTone(j.lastJobState)}>{j.lastJobState}</StatusPill>
        ) : (
          '—'
        ),
      width: 130,
    },
  ];

  return (
    <Table
      columns={columns}
      rows={jobs ?? []}
      rowKey={(j) => j.id}
      loading={isPending}
      emptyMessage="No recurring jobs."
      rowActions={(j) => (
        <Button variant="secondary" leftIcon={<Icon name="send" size={13} />} onClick={() => onTrigger(j.id)}>
          Trigger now
        </Button>
      )}
    />
  );
}

function RecentJobs() {
  const { tab } = useSearch({ from: '/admin/jobs/' });
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [queue, setQueue] = useState<string | undefined>(undefined);
  // Row click opens the overview detail (state timeline + exception for failed jobs).
  const [openJobId, setOpenJobId] = useState<string | null>(null);

  const { data: queues } = jobsApi.useQueues();
  const { data, isPending } = jobsApi.useJobList(tab, {
    from: page * pageSize,
    count: pageSize,
    queue: tab === 'enqueued' ? queue : undefined,
  });

  const toast = useToast();
  const { openModal } = useModal();
  const requeue = jobsApi.useRequeueJob();
  const remove = jobsApi.useDeleteJob();

  const setTab = (next: JobListKind) => {
    setPage(0);
    navigate({ to: '/admin/jobs', search: { tab: next } });
  };

  const onRequeue = async (j: JobSummary) => {
    try {
      await requeue.mutateAsync(j.id);
      toast.show({ title: 'Job requeued', severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to requeue',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  const onDelete = (j: JobSummary) => {
    openModal(
      <ConfirmDialog
        title="Delete job"
        message={`Delete job ${j.id}? This removes it from Hangfire.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await remove.mutateAsync(j.id);
            toast.show({ title: 'Job deleted', severity: 'success' });
          } catch (err) {
            toast.show({
              title: 'Failed to delete',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  const timestampHeader =
    tab === 'failed'
      ? 'Failed at'
      : tab === 'succeeded'
        ? 'Succeeded at'
        : tab === 'scheduled'
          ? 'Scheduled for'
          : tab === 'processing'
            ? 'Started at'
            : 'Enqueued at';

  const columns: TableColumn<JobSummary>[] = [
    {
      id: 'id',
      header: 'Id',
      cell: (j) => <code style={{ fontSize: 12, color: 'var(--text-3)' }}>{j.id}</code>,
      width: 220,
    },
    { id: 'job', header: 'Job', cell: (j) => j.job },
    {
      id: 'state',
      header: 'State',
      cell: (j) => <StatusPill status={jobStateTone(j.state)}>{j.state}</StatusPill>,
      width: 130,
    },
    {
      id: 'timestamp',
      header: timestampHeader,
      cell: (j) => formatDateTime(j.timestamp) ?? '—',
      width: 170,
    },
  ];

  if (tab === 'failed') {
    columns.push({
      id: 'reason',
      header: 'Reason',
      cell: (j) => (
        <div style={{ maxWidth: 360 }}>
          <div style={{ color: 'var(--text-2)' }}>{j.reason ?? j.exceptionType ?? '—'}</div>
          {j.exceptionMessage && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{j.exceptionMessage}</div>
          )}
        </div>
      ),
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          borderBottom: '1px solid var(--border)',
        }}
      >
        {JOB_TABS.map((t) => {
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
                color: active ? 'var(--text-1)' : 'var(--text-3)',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
        {tab === 'enqueued' && (queues?.length ?? 0) > 0 && (
          <div style={{ marginLeft: 'auto', minWidth: 180 }}>
            <Select
              value={queue ?? queues?.[0]?.name ?? ''}
              onChange={(e) => {
                setPage(0);
                setQueue(e.target.value);
              }}
              options={(queues ?? []).map((q) => ({
                value: q.name,
                label: `${q.name} (${q.length})`,
              }))}
            />
          </div>
        )}
      </div>

      <Table
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(j) => j.id}
        loading={isPending}
        emptyMessage="No jobs."
        onRowClick={(j) => setOpenJobId(j.id)}
        rowActions={(j) => (
          <>
            {REQUEUEABLE.includes(tab) && (
              <IconButton
                icon="refresh"
                label="Requeue"
                size="sm"
                onClick={() => onRequeue(j)}
              />
            )}
            <IconButton
              icon="trash"
              label="Delete"
              size="sm"
              variant="danger"
              onClick={() => onDelete(j)}
            />
          </>
        )}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(0);
          setPageSize(size);
        }}
      />

      {openJobId && (
        <JobDetailModal
          jobId={openJobId}
          canRequeue={REQUEUEABLE.includes(tab)}
          onClose={() => setOpenJobId(null)}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/jobs/')({
  validateSearch: searchSchema,
  beforeLoad: () => {
    if (!isSuperAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: JobsRoute,
  errorComponent: ErrorComponent,
});
